import type { ManualCampaignForm } from '@/features/campaigns/types/campaign';
import {
  MAX_AD_DURATION,
  MIN_AD_DURATION,
} from '@/features/campaigns/constants/campaignOptions';
import {
  todayIsoDate,
  tomorrowIsoDate,
  validateCampaignDateRange,
} from '@/features/campaigns/utils/campaignUtils';

/** Validates the manual builder's goal/creative/date step. Returns a field-keyed error map. */
export function validateManualStepOne(
  campaignData: ManualCampaignForm,
  options?: {
    requireAgencyClient?: boolean;
    allowToday?: boolean;
    instantAdAccess?: boolean;
  },
): Record<string, string> {
  const next: Record<string, string> = {};
  if (options?.requireAgencyClient && !campaignData.agencyClientId) {
    next.agencyClientId = 'Please select a client';
  }
  if (!campaignData.campaignGoals) {
    next.campaignGoals = 'Please select a campaign objective';
  }
  if (!campaignData.campaignName.trim()) {
    next.campaignName = 'Please enter a campaign name';
  }
  if (!campaignData.targetCities.length) {
    next.targetCities = 'Please select at least one target city';
  }
  if (!campaignData.mediaIds?.length && !campaignData.galleryId) {
    next.mediaId = 'Please upload a creative (image or video)';
  } else if (
    campaignData.mediaType?.startsWith('video') &&
    Number(campaignData.customDuration) > Number(campaignData.mediaDuration)
  ) {
    next.customDuration = `Duration cannot exceed video length of ${campaignData.mediaDuration}s`;
  } else if (
    !campaignData.customDuration ||
    Number(campaignData.customDuration) < MIN_AD_DURATION
  ) {
    next.customDuration = `Minimum duration is ${MIN_AD_DURATION} seconds`;
  } else if (Number(campaignData.customDuration) > MAX_AD_DURATION) {
    next.customDuration = `Maximum duration is ${MAX_AD_DURATION} seconds`;
  }
  if (!campaignData.adType) next.adType = 'Please select an ad category';

  const minStart =
    options?.allowToday || options?.instantAdAccess
      ? todayIsoDate()
      : tomorrowIsoDate();
  Object.assign(
    next,
    validateCampaignDateRange(
      campaignData.startDate,
      campaignData.endDate,
      minStart,
    ),
  );

  return next;
}
